import {Component, EventEmitter, forwardRef, inject, Input, NgModule, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

export class SdRadioChange {
  constructor(public source: SdRadioButton, public value: unknown) {}
}

@Component({
  selector: 'sd-radio-group',
  standalone: true,
  template: `<ng-content></ng-content>`,
  host: {class: 'sd-radio-group block', role: 'radiogroup'},
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SdRadioGroup), multi: true}],
})
export class SdRadioGroup implements ControlValueAccessor {
  @Input() value: unknown;
  @Input() disabled = false;
  @Input() name = `sd-radio-${Math.random().toString(36).slice(2)}`;
  @Output() change = new EventEmitter<SdRadioChange>();
  private onChange: (v: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: unknown): void {
    this.value = v;
  }
  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  select(button: SdRadioButton): void {
    this.value = button.value;
    this.onChange(button.value);
    this.onTouched();
    this.change.emit(new SdRadioChange(button, button.value));
  }
}

@Component({
  selector: 'sd-radio-button',
  standalone: true,
  template: `
    <label class="inline-flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="radio"
        class="h-4 w-4"
        [checked]="checked"
        [disabled]="disabled"
        [name]="name"
        (change)="onNative()"
      />
      <span><ng-content></ng-content></span>
    </label>
  `,
  host: {class: 'sd-radio inline-flex'},
})
export class SdRadioButton {
  @Input() value: unknown;
  @Input() checked = false;
  @Input() disabled = false;
  @Input() name = '';
  @Input() color: string | undefined;
  @Output() change = new EventEmitter<SdRadioChange>();
  readonly group = inject(SdRadioGroup, {optional: true, skipSelf: true});

  onNative(): void {
    this.checked = true;
    this.group?.select(this);
    this.change.emit(new SdRadioChange(this, this.value));
  }
}

@NgModule({
  imports: [SdRadioGroup, SdRadioButton],
  exports: [SdRadioGroup, SdRadioButton],
})
export class SdRadioModule {}
